import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Search, User } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ListItemSkeleton } from '../../components/shared/ClientPageSkeleton';

const ClientMessages = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Mock data - replace with actual API calls
  const conversations = [
    {
      id: 1,
      name: 'Vendor Name',
      lastMessage: 'Thank you for your booking!',
      timestamp: '2 hours ago',
      unread: 2
    },
    {
      id: 2,
      name: 'Support Team',
      lastMessage: 'We have received your inquiry',
      timestamp: '1 day ago',
      unread: 0
    }
  ];

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // Add message sending logic here
      setNewMessage('');
    }
  };

  const layoutProps = {
    title: 'Site Messages',
    subtitle: 'Review conversations with vendors and support.',
    guideKey: 'messages',
  };

  if (isLoading) {
    return (
      <ClientPageLayout {...layoutProps}>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
          <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)]">
            <div className="w-full lg:w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 !rounded-xl animate-pulse"></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ListItemSkeleton count={5} />
              </div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl mx-auto mb-4 animate-pulse"></div>
                <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mx-auto animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout {...layoutProps}>
      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <div className="w-full lg:w-1/3 border-r border-gray-200/80 dark:border-gray-700 flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-gray-200/80 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-sky-300 dark:text-sky-700" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                conversations.map((conversation) => (
                  <button
                    type="button"
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation.id)}
                    className={`w-full text-left p-4 border-b border-gray-200/80 dark:border-gray-700 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                      selectedConversation === conversation.id ? 'bg-sky-50 dark:bg-sky-950/30' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-800 rounded-xl flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-sky-700 dark:text-sky-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                            {conversation.name}
                          </h3>
                          {conversation.unread > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-800 border border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50">
                              {conversation.unread}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {conversation.lastMessage}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {conversation.timestamp}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50 dark:bg-gray-950/30">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                      <p className="text-sm">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${
                          message.sender === 'me' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2.5 !rounded-2xl ${
                            message.sender === 'me'
                              ? 'bg-sky-600 text-white'
                              : 'bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700 text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          <p className="text-sm">{message.text}</p>
                          <p className="text-xs mt-1 opacity-70">
                            {message.timestamp}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message Input */}
                <div className="border-t border-gray-200/80 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 !rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500"
                    />
                    <button
                      type="button"
                      onClick={handleSendMessage}
                      className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2.5 !rounded-xl transition-colors flex items-center gap-2 font-medium"
                    >
                      <Send className="w-4 h-4" />
                      <span>Send</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl border border-sky-100 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40 flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-sky-600 dark:text-sky-400" />
                  </div>
                  <p className="text-sm">Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientPageLayout>
  );
};

export default ClientMessages;
